import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { AuditLog, AuditAction } from '../types';

class AuditService {
  private readonly AUDIT_LOGS_COLLECTION = 'auditLogs';

  /**
   * Log an admin action
   */
  async logAdminAction(
    action: AuditAction,
    details: Record<string, any> = {}
  ): Promise<void> {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        console.warn(
          'Attempted to log admin action without authenticated user'
        );
        return;
      }

      const auditLogRef = collection(db, this.AUDIT_LOGS_COLLECTION);

      await addDoc(auditLogRef, {
        adminId: currentUser.uid,
        adminEmail: currentUser.email || 'unknown',
        action,
        targetUserId: details.targetUserId || null,
        targetUserEmail: details.targetUserEmail || null,
        details,
        timestamp: serverTimestamp(),
        ipAddress: null, // Could be added with additional setup
      });

      console.log('Admin action logged:', action);
    } catch (error) {
      console.error('Error logging admin action:', error);
      // Don't throw error - audit logging should not break functionality
    }
  }

  /**
   * Get all audit logs with pagination
   */
  async getAuditLogs(recordLimit: number = 100): Promise<AuditLog[]> {
    try {
      const auditLogsRef = collection(db, this.AUDIT_LOGS_COLLECTION);
      const q = query(
        auditLogsRef,
        orderBy('timestamp', 'desc'),
        limit(recordLimit)
      );

      const querySnapshot = await getDocs(q);
      const auditLogs: AuditLog[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        auditLogs.push({
          id: doc.id,
          adminId: data.adminId,
          adminEmail: data.adminEmail,
          action: data.action,
          targetUserId: data.targetUserId,
          targetUserEmail: data.targetUserEmail,
          details: data.details,
          timestamp: data.timestamp?.toDate() || new Date(),
          ipAddress: data.ipAddress,
        });
      });

      return auditLogs;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return [];
    }
  }

  /**
   * Get audit logs filtered by action type
   */
  async getAuditLogsByAction(
    action: AuditAction,
    recordLimit: number = 50
  ): Promise<AuditLog[]> {
    try {
      const auditLogsRef = collection(db, this.AUDIT_LOGS_COLLECTION);
      const q = query(
        auditLogsRef,
        where('action', '==', action),
        orderBy('timestamp', 'desc'),
        limit(recordLimit)
      );

      const querySnapshot = await getDocs(q);
      const auditLogs: AuditLog[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        auditLogs.push({
          id: doc.id,
          adminId: data.adminId,
          adminEmail: data.adminEmail,
          action: data.action,
          targetUserId: data.targetUserId,
          targetUserEmail: data.targetUserEmail,
          details: data.details,
          timestamp: data.timestamp?.toDate() || new Date(),
          ipAddress: data.ipAddress,
        });
      });

      return auditLogs;
    } catch (error) {
      console.error('Error fetching filtered audit logs:', error);
      return [];
    }
  }

  /**
   * Get audit logs for specific admin
   */
  async getAuditLogsByAdmin(
    adminId: string,
    recordLimit: number = 50
  ): Promise<AuditLog[]> {
    try {
      const auditLogsRef = collection(db, this.AUDIT_LOGS_COLLECTION);
      const q = query(
        auditLogsRef,
        where('adminId', '==', adminId),
        orderBy('timestamp', 'desc'),
        limit(recordLimit)
      );

      const querySnapshot = await getDocs(q);
      const auditLogs: AuditLog[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        auditLogs.push({
          id: doc.id,
          adminId: data.adminId,
          adminEmail: data.adminEmail,
          action: data.action,
          targetUserId: data.targetUserId,
          targetUserEmail: data.targetUserEmail,
          details: data.details,
          timestamp: data.timestamp?.toDate() || new Date(),
          ipAddress: data.ipAddress,
        });
      });

      return auditLogs;
    } catch (error) {
      console.error('Error fetching admin audit logs:', error);
      return [];
    }
  }
}

export default new AuditService();
